import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import { Box, Typography, Paper, CircularProgress, Alert, Container, ThemeProvider, } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Heading from "./Heading";
import Footer from "./Footer";
import getTheme from "./Theme";
import { useProfileStore, useProfileAuthStore, restoreSession, } from "./store/ProfileStore";
import { forceLogin } from "./Auth/AuthUtils";
import { useLogger } from "./logger";
import { useCacheBusterStore } from "./util/UrlUtils";
const LOGPREFIX = "Unsubscribe: ";
const Unsubscribe = () => {
    const profile = useProfileStore((state) => state);
    const auth = useProfileAuthStore((state) => state);
    const cacheBusterStore = useCacheBusterStore((state) => state);
    const navigate = useNavigate();
    const logger = useLogger((state) => state);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);
    const [profileLoading, setProfileLoading] = useState(false);
    // Authentication check - same pattern as ProfileSettings
    useEffect(() => {
        logger.log(LOGPREFIX + "useEffect-auth");
        if (!auth.isSignedIn() || !auth.userId) {
            logger.log(LOGPREFIX + "User NOT logged in - Attempting restore...");
            if (!restoreSession(auth, logger)) {
                logger.log(LOGPREFIX + "User NOT logged in - Restore failed. Navigating...");
                forceLogin(navigate);
            }
            else {
                logger.log(LOGPREFIX + "User logged in via restore session");
            }
        }
        else {
            logger.log(LOGPREFIX + "User IS logged in");
        }
    }, [auth.isSignedIn(), auth.userId, navigate]);
    // Load profile when authenticated - same pattern as ProfileSettings
    useEffect(() => {
        logger.log(LOGPREFIX + "useEffect-profile");
        if (auth.isSignedIn() && auth.userId) {
            if (auth.userId &&
                !profile.ok &&
                !profile.done &&
                !profile.error &&
                !profile.start &&
                !profileLoading) {
                logger.log(LOGPREFIX + "useEffect-profile: Start profile load...");
                setProfileLoading(true);
                profile.loadProfile(auth.userId, auth, cacheBusterStore, logger);
            }
            if (profile.done) {
                logger.log(LOGPREFIX + "useEffect-profile: Done - set not loading");
                setProfileLoading(false);
            }
        }
        logger.log(LOGPREFIX + "useEffect-profile: done");
    }, [
        auth.isSignedIn(),
        auth.userId,
        profile.ok,
        profile.done,
        profile.error,
        profile.start,
        profileLoading,
    ]);
    // Handle unsubscribe when user is authenticated and profile is loaded
    useEffect(() => {
        if (auth.isSignedIn() &&
            auth.userId &&
            profile.done &&
            profile.profile &&
            !success &&
            !loading &&
            !error &&
            !profileLoading) {
            logger.log(LOGPREFIX + "Profile loaded, starting unsubscribe process");
            handleUnsubscribe();
        }
    }, [
        auth.isSignedIn(),
        auth.userId,
        profile.done,
        profile.profile,
        success,
        loading,
        error,
        profileLoading,
    ]);
    const handleUnsubscribe = () => {
        if (!profile.profile ||
            !auth.isSignedIn() ||
            !auth.userId ||
            !auth.bearer) {
            setError("User is not signed in.");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            logger.log(LOGPREFIX + "Starting unsubscribe process");
            // Create subscription preferences with email notifications disabled
            const subscriptions = {
                emailNotifications: false,
            };
            logger.log(LOGPREFIX +
                "Saving updated subscriptions: " +
                JSON.stringify(subscriptions));
            // Create callbacks for the save operation - same pattern as ProfileSettings
            const callbacks = {
                okCallback: (clientDetailCode) => {
                    logger.log(LOGPREFIX +
                        "Successfully unsubscribed from email notifications, code: " +
                        clientDetailCode);
                    setLoading(false);
                    setSuccess(true);
                },
                errorCallback: (clientDetailCode, errorMessage) => {
                    logger.error(LOGPREFIX +
                        "Error unsubscribing from emails, code: " +
                        clientDetailCode +
                        ", message: " +
                        errorMessage);
                    setLoading(false);
                    setError("Failed to unsubscribe from email notifications. Please try again. (" +
                        clientDetailCode +
                        ")");
                },
            };
            // Save the updated profile with email notifications disabled
            // Using the same pattern as ProfileSettings saveNotifications function
            profile.saveProfile(auth, callbacks, undefined, // displayName - keep current
            undefined, // email - keep current
            undefined, // code - not needed
            undefined, // termsAccepted - keep current
            cacheBusterStore, logger, subscriptions);
        }
        catch (err) {
            logger.error(LOGPREFIX + "Error in handleUnsubscribe: " + err);
            setError("Failed to unsubscribe from email notifications. Please try again.");
            setLoading(false);
        }
    };
    // Content component for the main unsubscribe UI
    const UnsubscribeContent = () => {
        // Show loading while checking authentication or loading profile
        if (!auth.isSignedIn() ||
            !auth.userId ||
            profileLoading ||
            (!profile.done && !profile.error)) {
            return (_jsx(Box, Object.assign({ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }, { children: _jsx(CircularProgress, {}) })));
        }
        // Show error if profile failed to load
        if (profile.error) {
            return (_jsx(Box, Object.assign({ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", p: 3 }, { children: _jsxs(Paper, Object.assign({ elevation: 3, sx: {
                        p: 4,
                        maxWidth: 500,
                        width: "100%",
                        textAlign: "center",
                    } }, { children: [_jsx(Typography, Object.assign({ variant: "h4", component: "h1", gutterBottom: true }, { children: "Email Unsubscribe" })), _jsx(Alert, Object.assign({ severity: "error", sx: { mt: 3 } }, { children: _jsx(Typography, Object.assign({ variant: "body1" }, { children: "Failed to load your profile. Please try again later." })) }))] })) })));
        }
        return (_jsx(Box, Object.assign({ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", p: 3 }, { children: _jsxs(Paper, Object.assign({ elevation: 3, sx: {
                    p: 4,
                    maxWidth: 500,
                    width: "100%",
                    textAlign: "center",
                } }, { children: [_jsx(Typography, Object.assign({ variant: "h4", component: "h1", gutterBottom: true }, { children: "Email Unsubscribe" })), loading && (_jsxs(Box, Object.assign({ mt: 3 }, { children: [_jsx(CircularProgress, {}), _jsx(Typography, Object.assign({ variant: "body1", sx: { mt: 2 } }, { children: "Updating your email preferences..." }))] }))), success && (_jsxs(Alert, Object.assign({ severity: "success", sx: { mt: 3 } }, { children: [_jsx(Typography, Object.assign({ variant: "h6", gutterBottom: true }, { children: "Successfully Unsubscribed" })), _jsx(Typography, Object.assign({ variant: "body1" }, { children: "You have been unsubscribed from all email notifications from MediaShare.Link." })), _jsx(Typography, Object.assign({ variant: "body2", sx: { mt: 2 } }, { children: "You can update your email preferences anytime in your profile settings." }))] }))), error && (_jsx(Alert, Object.assign({ severity: "error", sx: { mt: 3 } }, { children: _jsx(Typography, Object.assign({ variant: "body1" }, { children: error })) }))), !loading && !success && !error && (_jsx(Typography, Object.assign({ variant: "body1", sx: { mt: 2 } }, { children: "Processing your unsubscribe request..." })))] })) })));
    };
    // Main output with standard layout structure
    const output = (_jsx("div", Object.assign({ style: { width: "100%", padding: 0, margin: 0 }, id: "outer" }, { children: _jsxs(ThemeProvider, Object.assign({ theme: getTheme() }, { children: [_jsx(Heading, {}), _jsx(Container, Object.assign({ sx: { backgroundColor: "#FFFFFF", width: "100%" } }, { children: _jsx(UnsubscribeContent, {}) })), _jsx(Footer, {})] })) })));
    return output;
};
export default Unsubscribe;
